import type { Router } from 'vue-router';
import { saveProjectDetailEngineeringTab } from '@/utils/projectDetailTabState';

export type BomDrawingReviewReportNav = {
  projectId: number;
  taskId: string;
  fileName: string;
  sourceObjectKey?: string;
};

export type BomDrawingReviewOpenIntent = BomDrawingReviewReportNav & {
  action: 'view';
};

const PENDING_KEY = 'aipd.pendingBomDrawingReviewOpen';

export function stashBomDrawingReviewOpenIntent(intent: BomDrawingReviewOpenIntent) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(intent));
  } catch {
    // ignore quota / private mode
  }
}

export function consumeBomDrawingReviewOpenIntent(): BomDrawingReviewOpenIntent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const o = parsed as Record<string, unknown>;
    const projectId = Number(o.projectId ?? o.project_id);
    const taskId = String(o.taskId ?? o.task_id ?? '').trim();
    const fileName = String(o.fileName ?? o.file_name ?? 'drawing.pdf').trim() || 'drawing.pdf';
    if (!Number.isFinite(projectId) || projectId <= 0 || !taskId) return null;
    const sourceObjectKey = String(o.sourceObjectKey ?? o.source_object_key ?? '').trim();
    return {
      projectId,
      taskId,
      fileName,
      sourceObjectKey: sourceObjectKey || undefined,
      action: 'view',
    };
  } catch {
    return null;
  }
}

export function parseBomDrawingReviewReportNav(raw: unknown): BomDrawingReviewReportNav | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const projectId = Number(o.projectId ?? o.project_id);
  const taskId = String(o.taskId ?? o.task_id ?? '').trim();
  const fileName = String(o.fileName ?? o.file_name ?? 'drawing.pdf').trim() || 'drawing.pdf';
  if (!Number.isFinite(projectId) || projectId <= 0 || !taskId) return null;
  const sourceObjectKey = String(o.sourceObjectKey ?? o.source_object_key ?? '').trim();
  return {
    projectId,
    taskId,
    fileName,
    sourceObjectKey: sourceObjectKey || undefined,
  };
}

export async function navigateToProjectBomDrawingReview(router: Router, projectId: number) {
  saveProjectDetailEngineeringTab(projectId, 'bom');
  const current = router.currentRoute.value;
  if (current.name === 'ProjectDashboard' && String(current.params.id) === String(projectId)) {
    window.dispatchEvent(
      new CustomEvent('aipd:open-pending-bom-drawing-review', { detail: { projectId } }),
    );
    return;
  }
  await router.push({ name: 'ProjectDashboard', params: { id: String(projectId) } });
}

export const BOM_DRAWING_REVIEW_OPEN_EVENT = 'aipd:open-pending-bom-drawing-review';
